"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/features/profile/hooks/useProfile";
import { useAuthStore } from "@/store/useAuthStore";

/**
 * THE SIGNED-IN USER AS THE BACKEND HAS IT NOW.
 *
 * The auth store is persisted, and only a sign-in or a save on /settings
 * wrote its user. A handle changed once (on another device, in another tab, or
 * before the settings page wrote the response back) therefore lived on in this
 * browser, and every call built from it — `/artists/my-collections/{username}`,
 * `/artists/my-nfts/{username}`, the profile link — kept asking for the old one.
 *
 * So the profile (`["profile"]`, five minutes fresh) is read once a session
 * is known and written into the store, merged over what the sign-in stored.
 * A changed handle also drops the lists cached under the old one. A profile of
 * another account (a cache left from before a sign-out) is never merged in.
 */
export function useFreshSessionUser() {
  const $ = _c(7);
  const token = useAuthStore(_temp);
  const setUser = useAuthStore(_temp2);
  const queryClient = useQueryClient();
  const t0 = Boolean(token);
  let t1;
  if ($[0] !== t0) {
    t1 = {
      enabled: t0
    };
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data
  } = useProfile(t1);
  const fresh = data?.data?.user;
  let t2;
  let t3;
  if ($[2] !== fresh || $[3] !== queryClient || $[4] !== setUser) {
    t2 = () => {
      if (!fresh) {
        return;
      }
      const {
        token: live,
        user: current
      } = useAuthStore.getState();
      if (!live) {
        return;
      }
      if (current?.id != null && fresh.id != null && String(current.id) !== String(fresh.id)) {
        return;
      }
      if (current?.username && fresh.username && current.username !== fresh.username) {
        queryClient.removeQueries({
          queryKey: ["my-nfts", current.username]
        });
        queryClient.removeQueries({
          queryKey: ["my-collections", current.username]
        });
      }
      setUser({
        ...(current ?? {}),
        ...fresh
      } as never);
    };
    t3 = [fresh, queryClient, setUser];
    $[2] = fresh;
    $[3] = queryClient;
    $[4] = setUser;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
}
function _temp2(s_0) {
  return s_0.setUser;
}
function _temp(s) {
  return s.token;
}